import type { Channel } from '@nexo/types';
import { Avatar, CountBadge, cn } from '@nexo/ui';
import {
  BellOff,
  Bookmark,
  CalendarClock,
  CalendarDays,
  ChevronDown,
  Download,
  Hash,
  ListTodo,
  Lock,
  Plus,
  UserPlus,
  Volume2,
} from 'lucide-react';
import { useState, type MouseEventHandler, type ReactNode } from 'react';
import { Link, NavLink } from 'react-router';
import { CallIndicator, useChannelCall } from '../calls/CallControls';
import { useCall } from '../calls/callStore';
import { useTeam } from '../../layouts/TeamContext';
import { useMeetings, useTasks } from '../../lib/queries';
import { useRealtime, type RealtimeStatus } from '../../realtime/RealtimeProvider';
import { useMe } from '../auth/AuthProvider';
import { UserBadges } from '../status/UserBadges';
import { badgeCount, effectiveLevel, showsUnread } from '../notifications/channelNotify';
import { AppVersion } from '../../components/AppVersion';
import { can } from '../../lib/permissions';
import { isDesktop } from '../../lib/platform';
import { t as translate } from '../../lib/i18n';
import { useTranslation } from 'react-i18next';
import { useChannelMenu } from './useChannelMenu';

export function channelLabel(channel: Channel): string {
  return channel.kind === 'dm'
    ? (channel.dmPeer?.displayName ?? translate('Conversación'))
    : channel.name;
}

const COLLAPSED_KEY = 'nexo.sidebar.collapsed';

export function ChannelSidebar() {
  const { team, channels, online, openDialog, memberById } = useTeam();
  const me = useMe();
  const tasks = useTasks(team.id);
  const { status } = useRealtime();
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState<string[]>(
    () => JSON.parse(localStorage.getItem(COLLAPSED_KEY) ?? '[]') as string[],
  );
  const channelMenu = useChannelMenu();
  const toggle = (key: string) => {
    const next = collapsed.includes(key) ? collapsed.filter((k) => k !== key) : [...collapsed, key];
    localStorage.setItem(COLLAPSED_KEY, JSON.stringify(next));
    setCollapsed(next);
  };

  const regular = channels.filter((c) => c.kind === 'channel' && c.mode === 'text');
  const voice = channels.filter((c) => c.kind === 'channel' && c.mode === 'voice');
  const dms = channels
    .filter((c) => c.kind === 'dm')
    .sort((a, b) => (b.lastMessageAt ?? b.createdAt).localeCompare(a.lastMessageAt ?? a.createdAt));
  const meetings = useMeetings(team.id);
  const todayMeetings = (meetings.data ?? []).filter(
    (m) =>
      !m.cancelledAt &&
      new Date(m.endsAt) > new Date() &&
      new Date(m.startsAt).toDateString() === new Date().toDateString() &&
      m.attendees.some((a) => a.id === me.id && a.response !== 'no'),
  ).length;
  const myOpenTasks = (tasks.data ?? []).filter(
    (t) => t.status !== 'done' && t.assignees.some((a) => a.id === me.id),
  ).length;

  return (
    <aside className="flex w-60 shrink-0 flex-col bg-canvas [--avatar-ring:var(--color-canvas)]">
      <nav className="scrollbar-thin flex-1 overflow-y-auto px-2.5 pt-4 pb-3">
        <SidebarLink
          to={`/app/t/${team.id}/tasks`}
          icon={<ListTodo className="size-4" />}
          label={t('Mis tareas')}
          count={myOpenTasks}
        />
        <SidebarLink
          to={`/app/t/${team.id}/meetings`}
          icon={<CalendarClock className="size-4" />}
          label={t('Reuniones')}
          count={todayMeetings}
        />
        <SidebarLink
          to={`/app/t/${team.id}/calendar`}
          icon={<CalendarDays className="size-4" />}
          label={t('Calendario')}
        />
        <SidebarLink
          to={`/app/t/${team.id}/saved`}
          icon={<Bookmark className="size-4" />}
          label={t('Guardados')}
        />

        <Section
          id="channels"
          title={t('Canales')}
          collapsed={collapsed.includes('channels')}
          onToggle={() => toggle('channels')}
          addLabel={t('Añadir canal')}
          onAdd={can(team, 'create_channels') ? () => openDialog('channel') : undefined}
        >
          {regular.map((channel) => (
            <SidebarLink
              key={channel.id}
              to={`/app/t/${team.id}/c/${channel.id}`}
              icon={channel.isPrivate ? <Lock className="size-3.5" /> : <Hash className="size-4" />}
              label={channel.name}
              {...notifyProps(channel)}
              onContextMenu={channelMenu.open(channel)}
              extra={<CallIndicator channelId={channel.id} />}
            />
          ))}
        </Section>

        <Section
          id="voice"
          title={t('Canales de voz')}
          collapsed={collapsed.includes('voice')}
          onToggle={() => toggle('voice')}
          addLabel={t('Añadir canal de voz')}
          onAdd={can(team, 'create_channels') ? () => openDialog('voice') : undefined}
        >
          {voice.map((channel) => (
            <VoiceChannelLink
              key={channel.id}
              channel={channel}
              onContextMenu={channelMenu.open(channel)}
            />
          ))}
        </Section>

        <Section
          id="dms"
          title={t('Mensajes directos')}
          collapsed={collapsed.includes('dms')}
          onToggle={() => toggle('dms')}
          addLabel={t('Nuevo mensaje')}
          onAdd={() => openDialog('dm')}
        >
          {dms.map((channel) => (
            <SidebarLink
              key={channel.id}
              to={`/app/t/${team.id}/c/${channel.id}`}
              icon={
                <Avatar
                  id={channel.dmPeer?.id ?? channel.id}
                  name={channelLabel(channel)}
                  src={channel.dmPeer?.avatarUrl}
                  size="xs"
                  online={online && channel.dmPeer ? online.has(channel.dmPeer.id) : undefined}
                />
              }
              label={channelLabel(channel)}
              {...notifyProps(channel)}
              onContextMenu={channelMenu.open(channel)}
              extra={
                <>
                  {channel.dmPeer && memberById(channel.dmPeer.id) && (
                    <UserBadges user={memberById(channel.dmPeer.id) ?? channel.dmPeer} />
                  )}
                  <CallIndicator channelId={channel.id} />
                </>
              }
            />
          ))}
        </Section>
      </nav>

      <div className="px-2.5 pb-2.5 safe-bottom">
        <button
          type="button"
          onClick={() => openDialog('members')}
          className="mb-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] text-muted hover:bg-surface hover:text-text"
        >
          <UserPlus className="size-4" />
          {can(team, 'invite_members') ? t('Invitar al equipo') : t('Miembros del equipo')}
        </button>
        {!isDesktop && (
          <Link
            to="/download"
            className="mb-1 flex w-full items-center gap-2 rounded-lg px-2.5 py-1.5 text-[13px] text-muted hover:bg-surface hover:text-text"
          >
            <Download className="size-4" />
            {t('Descargar la app')}
          </Link>
        )}
        <ConnectionStatus status={status} />
      </div>
      {channelMenu.element}
    </aside>
  );
}

function Section({
  title,
  collapsed,
  onToggle,
  addLabel,
  onAdd,
  children,
}: {
  id: string;
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  addLabel: string;
  /** Hidden when the user cannot add anything here. */
  onAdd?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="mt-5">
      <button
        type="button"
        aria-expanded={!collapsed}
        onClick={onToggle}
        className="mb-1 flex w-full items-center gap-1 px-1.5 text-[11px] font-bold tracking-wider text-subtle uppercase hover:text-muted"
      >
        <ChevronDown className={cn('size-3.5 transition-transform', collapsed && '-rotate-90')} />
        {title}
      </button>
      {!collapsed && (
        <div className="flex flex-col gap-0.5">
          {children}
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="flex h-8 items-center gap-2 rounded-lg px-2.5 text-[13px] text-subtle hover:bg-surface hover:text-text"
            >
              <span className="flex w-4 justify-center">
                <Plus className="size-3.5" />
              </span>
              {addLabel}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Voice channel entry with the people currently in it (Discord-style). */
function VoiceChannelLink({
  channel,
  onContextMenu,
}: {
  channel: Channel;
  onContextMenu: MouseEventHandler;
}) {
  const { team } = useTeam();
  const live = useChannelCall(channel.id);
  const current = useCall();
  const inCall = current?.join.channelId === channel.id;
  return (
    <div>
      <SidebarLink
        to={`/app/t/${team.id}/c/${channel.id}`}
        icon={<Volume2 className={cn('size-4', inCall && 'text-green-600 dark:text-green-400')} />}
        label={channel.name}
        onContextMenu={onContextMenu}
      />
      {live && live.participants.length > 0 && (
        <ul className="mb-1 ml-7 flex flex-col gap-0.5 py-0.5">
          {live.participants.map((person) => (
            <li key={person.id} className="flex items-center gap-2 text-[13px] text-muted">
              <Avatar id={person.id} name={person.displayName} src={person.avatarUrl} size="xs" />
              <span className="truncate">{person.displayName}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Discord-style sidebar state of a conversation. */
function notifyProps(channel: Channel) {
  return {
    count: badgeCount(channel),
    unread: showsUnread(channel),
    muted: effectiveLevel(channel) === 'muted',
  };
}

function SidebarLink({
  to,
  icon,
  label,
  count = 0,
  unread = count > 0,
  muted = false,
  extra,
  onContextMenu,
}: {
  to: string;
  icon: ReactNode;
  label: string;
  /** Red badge (mentions, DMs, tasks). */
  count?: number;
  /** Bold with a dot: new messages that don't need your attention. */
  unread?: boolean;
  muted?: boolean;
  /** Shown before the unread counter (e.g. a live call marker). */
  extra?: ReactNode;
  onContextMenu?: MouseEventHandler;
}) {
  const { t } = useTranslation();
  return (
    <NavLink
      to={to}
      end
      onContextMenu={onContextMenu}
      className={({ isActive }) =>
        cn(
          'group flex h-8 items-center gap-2 rounded-lg px-2.5 text-sm transition-colors',
          isActive
            ? 'bg-selected font-medium text-selected-fg shadow-panel [--avatar-ring:var(--color-selected)]'
            : cn(
                'hover:bg-surface hover:text-text',
                unread ? 'font-semibold text-text' : 'text-muted',
                muted && 'opacity-50',
              ),
        )
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={cn(
              'flex w-4 shrink-0 justify-center',
              isActive ? 'text-selected-fg/70' : 'text-subtle',
            )}
          >
            {icon}
          </span>
          <span className="min-w-0 flex-1 truncate">{label}</span>
          {extra}
          {muted && (
            <BellOff className="size-3.5 shrink-0 text-subtle" aria-label={t('Silenciada')} />
          )}
          {count > 0 ? (
            <CountBadge count={count} />
          ) : (
            unread &&
            !isActive && (
              <span className="size-2 shrink-0 rounded-full bg-text" aria-label={t('Sin leer')} />
            )
          )}
        </>
      )}
    </NavLink>
  );
}

const statusText: Record<RealtimeStatus, string> = {
  connected: 'En tiempo real',
  connecting: 'Conectando…',
  disabled: 'Sincronización periódica',
  unavailable: 'Sin conexión en tiempo real',
};

function ConnectionStatus({ status }: { status: RealtimeStatus }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 px-2.5 text-xs text-subtle">
      <p className="flex min-w-0 flex-1 items-center gap-2" title={t(statusText[status])}>
        <span
          className={cn(
            'size-1.5 rounded-full',
            status === 'connected'
              ? 'bg-success'
              : status === 'connecting'
                ? 'animate-pulse bg-warning'
                : 'bg-subtle',
          )}
        />
        <span className="truncate">{t(statusText[status])}</span>
      </p>
      <AppVersion className="shrink-0" />
    </div>
  );
}
